import { normalizeTimerAudio, type TimerAudioConfig } from '../lib/timerAudioConfig';
/**
 * Hook for connecting to the /display WebSocket namespace.
 * Used by TV screen displays with a bounded screen-session credential.
 */

import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import {
  createTimerTransitionState,
  playTimerTransitionSound,
  reduceTimerTransition,
} from '../lib/timerTransitionSound';

export interface DisplayTimerPayload {
  level: number;
  remainingSeconds: number;
  isRunning: boolean;
  elapsedMs?: number;
  sampledAt?: string;
  isBreak: boolean;
  smallBlind: number;
  bigBlind: number;
  ante: number;
  nextSmallBlind?: number;
  nextBigBlind?: number;
  nextAnte?: number;
  nextLevel?: number;
  nextIsBreak?: boolean;
  activePlayers: number;
  totalEntries: number;
  prizePool: number;
  displayPrizePool?: number;
  tournamentName: string;
  rebuyCount?: number;
  addonCount?: number;
  startingChips?: number;
  lateRegLevels?: number;
  startedAt?: string | null;
}

interface UseDisplaySocketOptions {
  credential: string;
  enabled: boolean;
  audioConfig?: TimerAudioConfig;
  audioReady?: boolean;
}

export interface DisplayConfigUpdate {
  tournamentId: string | null;
  layout: string;
  insetPercent?: number;
}

export interface LeaderboardEntry {
  rank: number;
  playerName: string;
  chips: number;
  tableNo: number | null;
  seatNo: number | null;
}

interface DisplaySocketState {
  timerPayload: DisplayTimerPayload | null;
  connected: boolean;
  configUpdate: DisplayConfigUpdate | null;
  leaderboard: LeaderboardEntry[];
  /** Increments each time the socket reconnects (was disconnected then connected again) */
  reconnectCount: number;
}

export function displaySocketConnectionOptions(credential: string) {
  return { auth: { credential }, transports: ['websocket'] };
}

export function useDisplaySocket({
  credential,
  enabled,
  audioConfig = normalizeTimerAudio(undefined),
  audioReady = true,
}: UseDisplaySocketOptions): DisplaySocketState {
  const audioRef = useRef({ config: audioConfig, ready: audioReady });
  audioRef.current = { config: audioConfig, ready: audioReady };
  const socketRef = useRef<Socket | null>(null);
  const [timerPayload, setTimerPayload] = useState<DisplayTimerPayload | null>(null);
  const [connected, setConnected] = useState(false);
  const [configUpdate, setConfigUpdate] = useState<DisplayConfigUpdate | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [reconnectCount, setReconnectCount] = useState(0);
  const wasConnectedRef = useRef(false);
  const transitionStateRef = useRef(createTimerTransitionState());

  useEffect(() => {
    setTimerPayload(null);
    setConfigUpdate(null);
    setLeaderboard([]);
    setConnected(false);
    wasConnectedRef.current = false;
    transitionStateRef.current = createTimerTransitionState();
    if (!enabled || !credential) return;

    const apiBase =
      (import.meta as unknown as { env: { VITE_API_URL?: string } }).env.VITE_API_URL ?? '';
    const socket = io(`${apiBase}/display`, displaySocketConnectionOptions(credential));

    socketRef.current = socket;
    let displayedTournament: string | null | undefined;

    socket.on('connect', () => {
      // Detect reconnection (was previously connected, disconnected, now connected again)
      if (wasConnectedRef.current) {
        setReconnectCount((c) => c + 1);
        transitionStateRef.current = { ...transitionStateRef.current, silentBaseline: true };
      }
      wasConnectedRef.current = true;
      setConnected(true);
    });
    socket.on('disconnect', () => {
      setConnected(false);
    });

    socket.on('connect_error', (err) => {
      console.warn('[WS] display connect_error:', err.message);
      setConnected(false);
    });

    const acceptTimerPayload = (data: DisplayTimerPayload) => {
      const transition = reduceTimerTransition(transitionStateRef.current, data);
      transitionStateRef.current = transition.state;
      if (transition.event === 'single') void playTimerTransitionSound('single');
      else if (transition.event && audioRef.current.ready) {
        const cue = audioRef.current.config[transition.event];
        void playTimerTransitionSound(cue.sound, undefined, undefined, cue.volume);
      }
      setTimerPayload(data);
    };

    socket.on('display:timer:update', acceptTimerPayload);

    socket.on('display:blind:change', acceptTimerPayload);

    socket.on('display:prize_pool:updated', (data: { prizePool: string; totalEntries: number }) => {
      setTimerPayload((prev) => {
        if (!prev) return prev;
        const parsed = parseFloat(data.prizePool);
        return {
          ...prev,
          prizePool: Number.isFinite(parsed) ? parsed : prev.prizePool,
          totalEntries: data.totalEntries,
        };
      });
    });

    socket.on('display:config:updated', (data: DisplayConfigUpdate) => {
      if (displayedTournament !== undefined && displayedTournament !== data.tournamentId) {
        transitionStateRef.current = createTimerTransitionState();
        setTimerPayload(null);
        setLeaderboard([]);
      }
      displayedTournament = data.tournamentId;
      setConfigUpdate(data);
    });

    socket.on('display:leaderboard:update', (data: { leaderboard: LeaderboardEntry[] }) => {
      setLeaderboard(data.leaderboard ?? []);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [credential, enabled]);

  return { timerPayload, connected, configUpdate, leaderboard, reconnectCount };
}
